/**
 * Portfolio Connect theme system.
 *
 * Design goals:
 *  - Zero runtime CSS-in-JS dependency. We emit CSS variables on the modal
 *    root; every component reads them via `var(--pc-*)`.
 *  - Embedders can override a handful of primary tokens; everything else
 *    derives from them.
 *  - When `mode` is unset or `'auto'`, follow the *host app's* theme by
 *    inspecting the computed background of `<body>` / `<html>` (covers
 *    Tailwind `.dark`, shadcn `[data-theme]`, manual style toggles, etc.).
 *    Falls back to `prefers-color-scheme` only when the host root is
 *    effectively transparent.
 */

export type PortfolioConnectTheme = {
  mode?: 'light' | 'dark' | 'auto';
  /** Primary brand color (CTAs, focus rings). */
  primary?: string;
  /** Primary color when hovered/pressed. Defaults to a darker shade of `primary`. */
  primaryHover?: string;
  /** Text color used on top of `primary`. Defaults to white. */
  primaryForeground?: string;
  /** Optional accent color for secondary highlights. Defaults to `primary`. */
  accent?: string;
  /** Base radius in px. All component radii are derived from this. Default 12. */
  radius?: number;
  /** Font stack applied to the widget root. Defaults to system UI. */
  fontFamily?: string;
};

export type ResolvedMode = 'light' | 'dark';

/**
 * Inspect the host page's computed root backgrounds and infer a mode.
 * Returns `undefined` when both `<body>` and `<html>` are effectively
 * transparent so the caller can fall through to other heuristics.
 */
export function detectHostMode(): ResolvedMode | undefined {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return undefined;
  }
  // body first: most apps paint their theme background here. html as a
  // fallback for sites that paint on the root element instead.
  const roots = [document.body, document.documentElement];
  for (const el of roots) {
    if (!el) continue;
    const parsed = parseRgb(window.getComputedStyle(el).backgroundColor);
    if (!parsed) continue;
    const [r, g, b, a] = parsed;
    // Treat near-transparent backgrounds as "no signal" — the cascade
    // hasn't decided a theme on this element yet.
    if (a < 0.1) continue;
    // Perceptual brightness (Rec. 601). Coarse but ample for a binary
    // light/dark decision; full WCAG luminance would be overkill here.
    const brightness = (r * 0.299 + g * 0.587 + b * 0.114) / 255;
    return brightness < 0.5 ? 'dark' : 'light';
  }
  return undefined;
}

export function resolveMode(mode: PortfolioConnectTheme['mode']): ResolvedMode {
  if (mode === 'dark') return 'dark';
  if (mode === 'light') return 'light';
  // 'auto' or unset — prefer the host app's theme over the OS preference.
  const host = detectHostMode();
  if (host) return host;
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

/** Default font stack — system-ui first so we match the host app's look. */
export const DEFAULT_FONT =
  'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Default primary color: blue-600. */
const DEFAULT_PRIMARY = '#2563eb';
const DEFAULT_PRIMARY_HOVER = '#1d4ed8';

/**
 * Build the CSSProperties style object that sets all pc-* variables on the
 * widget root element.
 *
 * `resolvedMode` lets the caller override the auto-detection (e.g. when
 * subscribing to `prefers-color-scheme` via `useResolvedThemeMode` so
 * the modal restyles live as the OS theme toggles). When omitted, falls
 * back to the synchronous detection which is correct for first paint.
 */
export function buildThemeVariables(
  theme: PortfolioConnectTheme | undefined,
  resolvedMode?: ResolvedMode,
): React.CSSProperties {
  const mode = resolvedMode ?? resolveMode(theme?.mode);
  const primary = theme?.primary || DEFAULT_PRIMARY;
  const primaryHover = theme?.primaryHover || darken(primary, 0.08) || DEFAULT_PRIMARY_HOVER;
  const primaryFg = theme?.primaryForeground || '#ffffff';
  const accent = theme?.accent || primary;
  const radius = theme?.radius ?? 12;
  const font = theme?.fontFamily || DEFAULT_FONT;

  const palette = mode === 'dark' ? DARK_PALETTE : LIGHT_PALETTE;

  const vars: Record<string, string> = {
    '--pc-mode': mode,
    '--pc-color-primary': primary,
    '--pc-color-primary-hover': primaryHover,
    '--pc-color-primary-foreground': primaryFg,
    '--pc-color-primary-soft': withAlpha(primary, mode === 'dark' ? 0.18 : 0.08),
    '--pc-color-primary-soft-border': withAlpha(primary, mode === 'dark' ? 0.35 : 0.25),
    '--pc-color-accent': accent,

    '--pc-color-surface': palette.surface,
    '--pc-color-surface-subtle': palette.surfaceSubtle,
    '--pc-color-surface-muted': palette.surfaceMuted,
    '--pc-color-surface-strong': palette.surfaceStrong,

    '--pc-color-border': palette.border,
    '--pc-color-border-strong': palette.borderStrong,

    '--pc-color-text': palette.text,
    '--pc-color-text-muted': palette.textMuted,
    '--pc-color-text-faded': palette.textFaded,

    '--pc-color-success': palette.success,
    '--pc-color-success-bg': palette.successBg,
    '--pc-color-success-border': palette.successBorder,
    '--pc-color-error': palette.error,
    '--pc-color-error-bg': palette.errorBg,
    '--pc-color-error-border': palette.errorBorder,
    '--pc-color-warning': palette.warning,
    '--pc-color-warning-bg': palette.warningBg,
    '--pc-color-warning-border': palette.warningBorder,

    '--pc-color-overlay': mode === 'dark' ? 'rgba(0,0,0,0.65)' : 'rgba(15,23,42,0.55)',

    '--pc-radius-sm': `${Math.max(4, radius - 6)}px`,
    '--pc-radius-md': `${Math.max(6, radius - 2)}px`,
    '--pc-radius-lg': `${radius}px`,
    '--pc-radius-xl': `${radius + 4}px`,
    '--pc-radius-pill': '9999px',

    '--pc-shadow-card':
      mode === 'dark'
        ? '0 20px 60px rgba(0,0,0,0.55)'
        : '0 25px 80px rgba(15,23,42,0.18)',
    '--pc-shadow-button':
      mode === 'dark'
        ? `0 6px 18px ${withAlpha(primary, 0.45)}`
        : `0 6px 18px ${withAlpha(primary, 0.28)}`,

    '--pc-font-family': font,
  };

  // Broker-tile initial-letter avatar palette. Eight pairs because the
  // BrokerTile hashes the broker name into one of N. Light-mode uses
  // soft pastels with deep-text contrast; dark-mode uses muted backs
  // with light-text contrast — both pass WCAG AA on the surface.
  const brokerPalette = mode === 'dark' ? DARK_BROKER_PALETTE : LIGHT_BROKER_PALETTE;
  brokerPalette.forEach((p, i) => {
    vars[`--pc-color-tile-${i + 1}-bg`] = p.bg;
    vars[`--pc-color-tile-${i + 1}-fg`] = p.fg;
  });

  return vars as unknown as React.CSSProperties;
}

// ---------- Broker-tile palettes ----------

type TileColor = { bg: string; fg: string };

export const LIGHT_BROKER_PALETTE: TileColor[] = [
  { bg: '#dbeafe', fg: '#1e40af' }, // blue
  { bg: '#dcfce7', fg: '#166534' }, // green
  { bg: '#fef3c7', fg: '#92400e' }, // amber
  { bg: '#fce7f3', fg: '#9d174d' }, // pink
  { bg: '#ede9fe', fg: '#5b21b6' }, // violet
  { bg: '#ccfbf1', fg: '#115e59' }, // teal
  { bg: '#ffe4e6', fg: '#9f1239' }, // rose
  { bg: '#e0e7ff', fg: '#3730a3' }, // indigo
];

export const DARK_BROKER_PALETTE: TileColor[] = [
  { bg: 'rgba(59,130,246,0.20)', fg: '#bfdbfe' },  // blue
  { bg: 'rgba(34,197,94,0.20)', fg: '#bbf7d0' },   // green
  { bg: 'rgba(234,179,8,0.20)', fg: '#fde68a' },   // amber
  { bg: 'rgba(236,72,153,0.20)', fg: '#fbcfe8' },  // pink
  { bg: 'rgba(139,92,246,0.20)', fg: '#ddd6fe' },  // violet
  { bg: 'rgba(20,184,166,0.20)', fg: '#99f6e4' },  // teal
  { bg: 'rgba(244,63,94,0.20)', fg: '#fecdd3' },   // rose
  { bg: 'rgba(99,102,241,0.20)', fg: '#c7d2fe' },  // indigo
];

export const BROKER_PALETTE_SIZE = LIGHT_BROKER_PALETTE.length;

/**
 * Subscribe to whatever the resolved mode depends on so the widget
 * restyles live when the host (or OS) theme toggles. Watches:
 *  - `prefers-color-scheme` media query (OS-level)
 *  - `class`, `style`, and `data-theme` attribute mutations on `<html>`
 *    and `<body>` (host-level: Tailwind `.dark` toggle, shadcn
 *    `[data-theme]`, etc.)
 *
 * Skipped entirely when `mode` is an explicit `'light'` or `'dark'`.
 */
import { useEffect, useState } from 'react';
export function useResolvedThemeMode(mode: PortfolioConnectTheme['mode']): ResolvedMode {
  const [resolved, setResolved] = useState<ResolvedMode>(() => resolveMode(mode));
  useEffect(() => {
    setResolved(resolveMode(mode));
    if (mode === 'light' || mode === 'dark') return;
    if (typeof window === 'undefined') return;

    const reResolve = () => setResolved(resolveMode(mode));

    let media: MediaQueryList | null = null;
    if (window.matchMedia) {
      media = window.matchMedia('(prefers-color-scheme: dark)');
      if (media.addEventListener) media.addEventListener('change', reResolve);
      else media.addListener(reResolve);
    }

    // Most app-level theme switches set a class or data-theme attribute
    // on `<html>` or `<body>` (and the bg colour cascades). Watching
    // attribute changes on both covers the common conventions without
    // requiring the embedder to opt in.
    let observer: MutationObserver | null = null;
    if (typeof MutationObserver !== 'undefined' && typeof document !== 'undefined') {
      observer = new MutationObserver(reResolve);
      const opts: MutationObserverInit = {
        attributes: true,
        attributeFilter: ['class', 'style', 'data-theme'],
      };
      if (document.documentElement) observer.observe(document.documentElement, opts);
      if (document.body) observer.observe(document.body, opts);
    }

    return () => {
      if (media) {
        if (media.removeEventListener) media.removeEventListener('change', reResolve);
        else media.removeListener(reResolve);
      }
      if (observer) observer.disconnect();
    };
  }, [mode]);
  return resolved;
}

// ---------- Palettes ----------

type Palette = {
  surface: string;
  surfaceSubtle: string;
  surfaceMuted: string;
  surfaceStrong: string;
  border: string;
  borderStrong: string;
  text: string;
  textMuted: string;
  textFaded: string;
  success: string;
  successBg: string;
  successBorder: string;
  error: string;
  errorBg: string;
  errorBorder: string;
  warning: string;
  warningBg: string;
  warningBorder: string;
};

const LIGHT_PALETTE: Palette = {
  surface: '#ffffff',
  surfaceSubtle: '#f9fafb',
  surfaceMuted: '#f3f4f6',
  surfaceStrong: '#ffffff',
  border: '#e5e7eb',
  borderStrong: '#d1d5db',
  text: '#111827',
  textMuted: '#4b5563',
  textFaded: '#9ca3af',
  success: '#15803d',
  successBg: '#f0fdf4',
  successBorder: '#bbf7d0',
  error: '#b91c1c',
  errorBg: '#fef2f2',
  errorBorder: '#fecaca',
  warning: '#92400e',
  warningBg: '#fffbeb',
  warningBorder: '#fde68a',
};

const DARK_PALETTE: Palette = {
  surface: '#111827',
  surfaceSubtle: '#1f2937',
  surfaceMuted: '#0b1220',
  surfaceStrong: '#0b1220',
  border: '#273244',
  borderStrong: '#3b4558',
  text: '#f9fafb',
  textMuted: '#cbd5e1',
  textFaded: '#94a3b8',
  success: '#4ade80',
  successBg: 'rgba(34,197,94,0.12)',
  successBorder: 'rgba(34,197,94,0.35)',
  error: '#fca5a5',
  errorBg: 'rgba(239,68,68,0.15)',
  errorBorder: 'rgba(239,68,68,0.4)',
  warning: '#fbbf24',
  warningBg: 'rgba(234,179,8,0.14)',
  warningBorder: 'rgba(234,179,8,0.4)',
};

// ---------- Color utils ----------

function hexToRgb(hex: string): [number, number, number] | null {
  const h = hex.trim();
  if (!h.startsWith('#')) return null;
  const raw = h.slice(1);
  const v = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  if (v.length !== 6) return null;
  const num = parseInt(v, 16);
  if (Number.isNaN(num)) return null;
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${[r, g, b]
    .map((n) => clamp(n).toString(16).padStart(2, '0'))
    .join('')}`;
}

/** Darken a hex color by a fraction (0–1). Returns undefined if input isn't hex. */
function darken(hex: string, amount: number): string | undefined {
  const rgb = hexToRgb(hex);
  if (!rgb) return undefined;
  const [r, g, b] = rgb;
  return rgbToHex(r * (1 - amount), g * (1 - amount), b * (1 - amount));
}

/**
 * Produce a CSS color expression with the supplied alpha. Works for hex input;
 * for non-hex we wrap in `color-mix` which is widely supported in evergreen
 * browsers.
 */
function withAlpha(color: string, alpha: number): string {
  const rgb = hexToRgb(color);
  if (rgb) {
    const [r, g, b] = rgb;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return `color-mix(in srgb, ${color} ${Math.round(alpha * 100)}%, transparent)`;
}

/**
 * Parse the `rgb(...)` / `rgba(...)` strings that `getComputedStyle`
 * returns. Returns `[r, g, b, a]` with alpha defaulted to 1 when absent.
 */
function parseRgb(value: string): [number, number, number, number] | null {
  if (!value) return null;
  const m = value.match(/rgba?\(([^)]+)\)/i);
  if (!m) return null;
  const parts = m[1].split(',').map((s) => parseFloat(s.trim()));
  if (parts.length < 3) return null;
  const [r, g, b, a = 1] = parts;
  if ([r, g, b].some((v) => Number.isNaN(v))) return null;
  return [r, g, b, a];
}
